import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { fetchUserBrands, fetchUserBrandsWithStrategy, fetchUserBrands as fetchUserBrandThunk } from 'slices/users/brands/thunk';
import { UserBrand } from 'types/users';
import '../../../pages/Campaigns/style.scss';
import Loader from 'Components/Common/Loader';
import PerfectScrollbar from 'react-perfect-scrollbar'
import { formatNumber } from 'utils/helpers';
import InstagramLogoIcon from 'assets/icons/InstagramLogoIcon';

const CampaignCreationBrandSelection: React.FC<{ value: UserBrand | null, onChange: (brand: UserBrand | null) => void, mode: 'prompt' | 'strategy' }> = ({ value, onChange, mode }) => {
    const userBrandsWithStrategy = useSelector((state: RootState) => state.userBrands.items_with_strategy);
    const userBrandsWithoutStrategy = useSelector((state: RootState) => state.userBrands.items);
    const userBrands = mode === 'strategy' ? userBrandsWithStrategy : userBrandsWithoutStrategy;
    const dispatch: AppDispatch = useDispatch();
    const loading = useSelector((state: RootState) => state.userBrands.loading_with_strategy);
    const [selectedBrandId, setSelectedBrandId] = useState<number | null>(value?.id ?? null);

    useEffect(() => {
        if(mode === 'strategy') {
            dispatch(fetchUserBrandsWithStrategy(true));
        } else {
            dispatch(fetchUserBrands(true));
        }
    }, [])

    useEffect(() => {
        setSelectedBrandId(value?.id ?? null);
    }, [value?.id])

    const brandsWithSocial = useMemo(
        () => (userBrands ?? []).filter((ub) => Boolean(ub.social_account)),
        [userBrands]
    );

    const selectedBrand = useMemo(() => {
        if (!selectedBrandId) return null;
        return brandsWithSocial.find((b) => b.id === selectedBrandId) ?? null;
    }, [brandsWithSocial, selectedBrandId])

    if(loading) return <Loader />

    return (
        <div className="page-content pt-0 mt-0">
            <div className="new-campaign-creation-mode">
                <div className='new-campaign-creation-mode-header'>
                    <div className="d-flex flex-column justify-content-center align-items-center gap-2">
                        <h1 className="new-campaign-creation-mode-title">
                            Choose the brand you want to build AI-powered campaigns for.
                        </h1>
                        <p className='new-campaign-creation-mode-description'>
                            Select a brand to unlock smart campaign ideas, tailored creatives, and AI-driven growth strategies.
                        </p>
                    </div>
                </div>
                <div className="campaign-brand-picker" aria-label="Brand selection">
                    <div className="campaign-brand-picker__scroll">
                        <PerfectScrollbar options={{ suppressScrollX: true }}>
                            <div className="campaign-brand-picker__list" role="listbox" aria-label="Brands">
                                {brandsWithSocial.length === 0 && (
                                    <div className="campaign-brand-picker__empty">No brands with connected social accounts.</div>
                                )}

                                {brandsWithSocial.map((brand) => {
                                    const selected = selectedBrandId === brand.id;
                                    const sa = brand.social_account;
                                    const handleRaw = sa?.handle ?? '';
                                    const handle = handleRaw ? (handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`) : '';
                                    const platform = sa?.platform;
                                    const platformIcon = platform === 'ig' ? <InstagramLogoIcon />:(platform === 'yt' ? <i className='ri-youtube-fill text-danger' /> : null);
                                    const avatarUrl = sa?.profile_picture_url ?? '';
                                    const postsCount = sa?.ig?.media_count ?? 0;
                                    const followersCount = sa?.ig?.followers_count ?? 0;
                                    const followingCount = sa?.ig?.follows_count ?? 0;
                                    const videosCount = sa?.yt?.video_count ?? 0;
                                    const subscribersCount = sa?.yt?.subscriber_count ?? 0;
                                    const viewsCount = sa?.yt?.view_count ?? 0;

                                    return (
                                        <button
                                            key={brand.id}
                                            type="button"
                                            className={`campaign-brand-option${selected ? ' campaign-brand-option--selected' : ''}`}
                                            onClick={() => setSelectedBrandId(brand.id)}
                                            role="option"
                                            aria-selected={selected}
                                        >
                                            <div className="campaign-brand-option__left">
                                                <div className="campaign-brand-option__avatar" aria-hidden="true">
                                                    {avatarUrl ? (
                                                        <img src={avatarUrl} alt="" />
                                                    ) : (
                                                        <span className="campaign-brand-option__avatarFallback">
                                                            {(brand.name?.trim()?.[0] ?? 'B').toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="campaign-brand-option__meta">
                                                    <div className="campaign-brand-option__name">{brand.name}</div>
                                                    {handle && <div className="campaign-brand-option__handle"> {platformIcon} {handle}</div>}
                                                </div>
                                            </div>

                                            <div className="campaign-brand-option__stats" aria-label="Account stats">
                                                <div className="campaign-brand-option__stat">
                                                    <div className="campaign-brand-option__statValue">{platform === 'ig' ? formatNumber(postsCount) : (platform === 'yt' ? formatNumber(videosCount) : '-')}</div>
                                                    <div className="campaign-brand-option__statLabel">{platform === 'ig' ? 'No. of posts' : (platform === 'yt' ? 'No. of videos' : '-')}</div>
                                                </div>
                                                <div className="campaign-brand-option__stat">
                                                    <div className="campaign-brand-option__statValue">{platform === 'ig' ? formatNumber(followersCount) : (platform === 'yt' ? formatNumber(subscribersCount) : '-')}</div>
                                                    <div className="campaign-brand-option__statLabel">{platform === 'ig' ? 'Followers' : (platform === 'yt' ? 'Subscribers' : '-')}</div>
                                                </div>
                                                <div className="campaign-brand-option__stat">
                                                    <div className="campaign-brand-option__statValue">{platform === 'ig' ? formatNumber(followingCount) : (platform === 'yt' ? formatNumber(viewsCount) : '-')}</div>
                                                    <div className="campaign-brand-option__statLabel">{platform === 'ig' ? 'Following' : (platform === 'yt' ? 'Views' : '-')}</div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </PerfectScrollbar>
                    </div>

                    <div className="campaign-brand-picker__footer">
                        <button
                            type="button"
                            className="btn btn-gradient w-100"
                            disabled={!selectedBrand}
                            onClick={() => onChange(selectedBrand)}
                        >
                            BUILD WITH AI
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CampaignCreationBrandSelection